import {
  computed,
  effect,
  inject,
  Injectable,
  PLATFORM_ID,
  signal,
  WritableSignal,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { StockConflictItem } from '../models/cart.model';

/**
 * Estado global del drawer del carrito. El CartDrawerComponent (montado una
 * única vez en el root layout) lee `isOpen()`; el Header y otros triggers
 * (quick-add post Tarea 3b) invocan open()/close()/toggle().
 *
 * Bloquea el scroll del body mientras el drawer está abierto, para que los
 * triggers no tengan que preocuparse por ese efecto lateral.
 *
 * Además aloja el estado de CONFLICTO de stock: cuando la puerta de pago
 * devuelve un 409 con desglose por producto, el checkout llama
 * `openWithConflicts()` para reabrir el drawer con las líneas resaltadas y sus
 * unidades libres. El drawer las lee por `product_id` y las limpia
 * (`clearConflict`) a medida que el usuario ajusta o elimina cada línea.
 */
@Injectable({ providedIn: 'root' })
export class CartDrawerService {
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  readonly isOpen: WritableSignal<boolean> = signal(false);

  /** Conflictos activos indexados por `product_id` (fuente única del resaltado en el drawer). */
  private readonly _conflicts = signal<Map<number, StockConflictItem>>(new Map());
  readonly conflicts = this._conflicts.asReadonly();
  readonly hasConflicts = computed(() => this._conflicts().size > 0);

  constructor() {
    effect(() => {
      if (!this.isBrowser) return;
      document.body.style.overflow = this.isOpen() ? 'hidden' : '';
    });
  }

  open(): void {
    // Apertura MANUAL (quick-add, header…): arranca sin resaltado de conflicto. La limpieza vive tanto
    // aquí como en close() para que ninguna apertura manual herede conflictos aunque en el futuro se
    // añada un auto-cierre por navegación que no pase por close(). openWithConflicts() NO usa este
    // método (fija su propio Map).
    this._conflicts.set(new Map());
    this.isOpen.set(true);
  }

  close(): void {
    this.isOpen.set(false);
    // El resaltado no debe sobrevivir al cierre: la próxima apertura manual parte limpia.
    this._conflicts.set(new Map());
  }

  toggle(): void {
    this.isOpen.update((v) => !v);
  }

  /** Reabre el drawer con el desglose por producto del 409 de la puerta de stock. */
  openWithConflicts(items: StockConflictItem[]): void {
    const map = new Map<number, StockConflictItem>();
    for (const item of items) {
      map.set(item.product_id, item);
    }
    this._conflicts.set(map);
    this.isOpen.set(true);
  }

  /** Limpia el conflicto de una línea (tras ajustarla o eliminarla). Update inmutable. */
  clearConflict(productId: number): void {
    const current = this._conflicts();
    if (!current.has(productId)) return;
    const next = new Map(current);
    next.delete(productId);
    this._conflicts.set(next);
  }
}
